import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ChevronLeft, ChevronRight } from "lucide-react";

export function usePagination(items, defaultSize = 10) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultSize);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const filtered = items.filter(item => {
    const d = item.created_at?.slice(0, 10) || item.timestamp?.slice(0, 10) || item.data?.slice(0, 10) || "";
    if (dateFrom && d < dateFrom) return false;
    if (dateTo && d > dateTo) return false;
    return true;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const paged = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);

  const resetPage = () => setPage(1);

  return { paged, page: safePage, setPage, pageSize, setPageSize, dateFrom, setDateFrom, dateTo, setDateTo, totalPages, totalItems: filtered.length, resetPage };
}

export function PaginationBar({ page, setPage, pageSize, setPageSize, totalPages, totalItems, dateFrom, setDateFrom, dateTo, setDateTo, resetPage }) {
  return (
    <div className="flex flex-wrap items-center gap-3 mt-4 pt-3 border-t border-[#1e3456]">
      {/* Date filters */}
      <div className="flex items-center gap-2">
        <Label className="text-[9px] text-white/25">Da</Label>
        <Input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); resetPage(); }} className="bg-white/5 border-[#1e3456] text-white text-[10px] h-7 w-28 px-1.5" />
        <Label className="text-[9px] text-white/25">A</Label>
        <Input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); resetPage(); }} className="bg-white/5 border-[#1e3456] text-white text-[10px] h-7 w-28 px-1.5" />
        {(dateFrom || dateTo) && <button onClick={() => { setDateFrom(""); setDateTo(""); resetPage(); }} className="text-[9px] text-[#5ba8d9] hover:underline">Reset</button>}
      </div>

      <div className="flex-1" />

      {/* Info */}
      <span className="text-[10px] text-white/25">{totalItems} totali</span>

      {/* Page size */}
      <select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); resetPage(); }}
        className="h-7 rounded bg-white/5 border border-[#1e3456] text-white text-[10px] px-1.5 focus:outline-none">
        {[10, 20, 50, 100].map(n => <option key={n} value={n} className="bg-[#13243d]">{n}</option>)}
      </select>

      {/* Navigation */}
      <div className="flex items-center gap-1">
        <Button size="sm" variant="ghost" disabled={page <= 1} onClick={() => setPage(page - 1)}
          className="h-7 w-7 p-0 text-white/40 hover:text-white hover:bg-[#1a3a5c] disabled:opacity-20">
          <ChevronLeft className="w-3.5 h-3.5" />
        </Button>
        <span className="text-[10px] text-white/40 min-w-[60px] text-center">{page} / {totalPages}</span>
        <Button size="sm" variant="ghost" disabled={page >= totalPages} onClick={() => setPage(page + 1)}
          className="h-7 w-7 p-0 text-white/40 hover:text-white hover:bg-[#1a3a5c] disabled:opacity-20">
          <ChevronRight className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );
}
